// --------------------------------------------------------------
// Created On: 2026-07-27
// Author: Zachary Thomas
//
// Last Modified: 2026-08-05
// Modified By: Hannah Vaughan
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo } from "react";
import styles from "./DigitalOutputPage.module.scss";
import IconTooltip from "src/components/IconTooltip/IconTooltip";
import DigitalOutput from "./DigitalOutput/DigitalOutput";
import WarningList from "src/components/WarningList/WarningList";
import Warning from "src/components/Warning/Warning";

// Page of the Asset Management Wizard that allows the user to optionally add digital output sensors.
export default function DigitalOutputPage(props: Props): Component {
  const sensorPositions = useMemo(() => getSensorPositions(props.sensorCount), [props.sensorCount]);
  const localSensorPositionsInUse = useMemo(
    () => getLocalSensorPositionsInUse(props.digitalOutputCards),
    [JSON.stringify(props.digitalOutputCards)]
  );
  const [mainWarningMessage, warningMessages] = useMemo(
    () => getWarningMessages(props.digitalOutputsInUse),
    [JSON.stringify(props.digitalOutputsInUse)]
  );

  // Generates an array of numbers from 1 to X, where X is the max sensor count for digital outputs.
  function getSensorPositions(sensorCount: number): number[] {
    return new Array<number>(sensorCount).fill(0).map((_, i) => i + 1);
  }

  // Gathers a list of all digital output sensor positions that are in use by the current asset.
  function getLocalSensorPositionsInUse(digitalOutputCards: DigitalOutputCard[]): number[] {
    const newLocalSensorPositionsInUse: number[] = [];
    digitalOutputCards.forEach((card) => {
      newLocalSensorPositionsInUse.push(card.position);
    });
    return newLocalSensorPositionsInUse;
  }

  // Populate the list of warnings whenever there are any digital outputs in use by other assets in the network.
  function getWarningMessages(digitalOutputsInUse: SensorInfoMap | null): [string, string[]] {
    let newMainWarningMessage = "";
    const newWarningMessages: string[] = [];
    if (digitalOutputsInUse !== null && Object.keys(digitalOutputsInUse).length > 0) {
      newMainWarningMessage = "The following digital output sensors are already in use:";
      Object.entries(digitalOutputsInUse).forEach(([sensorPosition, sensor]) => {
        const formattedSensor: SensorInfo = sensor as SensorInfo;
        newWarningMessages.push(
          `Position #${sensorPosition} is already reporting '${formattedSensor.reporting}' ` +
            `for asset '${formattedSensor.assetName}'.`
        );
      });
    }
    return [newMainWarningMessage, newWarningMessages];
  }

  return (
    <div data-test="digital-outputs-page">
      {props.isModbusInUse && props.isScadaRtuDeviceType && (
        <div className="mx-1 my-2">
          <Warning
            message={"The SCADA RTU does not allow for digital outputs and Modbus to be in use at the same time."}
          />
        </div>
      )}
      <div className="mx-1 my-2">
        <label className={styles.title}>
          <span className="ms-3 me-2">Digital Output Sensors</span>
          <IconTooltip
            id="digital-output-sensors-tooltip"
            icon="info-circle"
            message={
              "Describes the position of output sensors and their labels for remote operation " +
              "(example: 'Running Status', 'ON', 'OFF'). The operation label will be displayed as the control header " +
              "on the remote operation page. While the label for CLOSED and OPEN signals will be shown on two " +
              "separate buttons beneath the header."
            }
            color="var(--info-tooltip)"
          />
        </label>
        <button
          data-test="add-digital-output-button"
          type="submit"
          className={`${styles.addButton} btn btn-success float-end`}
          onClick={() => props.onCreate()}
          disabled={
            props.digitalOutputCards.length >= props.sensorCount ||
            (props.digitalOutputsInUse !== null &&
              props.digitalOutputsInUse !== undefined &&
              Object.keys(props.digitalOutputsInUse).length >= props.sensorCount) ||
            props.disabled ||
            (props.isScadaRtuDeviceType && props.isModbusInUse)
          }
        >
          <span className="d-none d-sm-inline">Add Digital Output</span>
          <i className="d-inline d-sm-none fa fa-fw fa-plus fa-xs" />
        </button>
      </div>
      {props.digitalOutputCards.map((card, i) => (
        <DigitalOutput
          key={card.formId}
          formIndex={i}
          formId={card.formId}
          position={card.position}
          operationLabel={card.operationLabel}
          trueLabel={card.trueLabel}
          falseLabel={card.falseLabel}
          isRented={props.isRented}
          sensorPositions={sensorPositions}
          digitalInputsInUse={props.digitalOutputsInUse}
          localSensorPositionsInUse={localSensorPositionsInUse}
          disabled={props.disabled || (props.isScadaRtuDeviceType && props.isModbusInUse)}
          onCreate={() => props.onCreate()}
          onChange={(formId, position, operationLabel, trueLabel, falseLabel) =>
            props.onChange(formId, position, operationLabel, trueLabel, falseLabel)
          }
          onDelete={(formId) => props.onDelete(formId)}
        />
      ))}
      {mainWarningMessage.length > 0 && warningMessages.length > 0 && (
        <div className="m-3">
          <WarningList mainMessage={mainWarningMessage} messages={warningMessages} />
        </div>
      )}
    </div>
  );
}

interface Props {
  isRented: boolean;
  isModbusInUse: boolean;
  isScadaRtuDeviceType: boolean;
  sensorCount: number;
  digitalOutputCards: DigitalOutputCard[];
  digitalOutputsInUse: SensorInfoMap | null;
  deviceType: string | null;
  disabled: boolean;
  onCreate: () => void;
  onChange: (formId: number, position: number, operationLabel: string, trueLabel: string, falseLabel: string) => void;
  onDelete: (formId: number) => void;
}

interface DigitalOutputCard {
  formId: number;
  position: number;
  operationLabel: string;
  trueLabel: string;
  falseLabel: string;
}

interface SensorInfoMap {
  [sensorNumber: number]: SensorInfo;
}

interface SensorInfo {
  reporting: string;
  assetName: string;
}
